import React, { useEffect, useRef, useState } from 'react';
import {
  advanceLoginTransitionProgress,
  AUTH_TRANSITION_EVENT,
  AUTH_TRANSITION_MILESTONES,
  AUTH_TRANSITION_PAINTED_EVENT,
  readLoginTransitionProgress,
} from './loginTransitionProgress';
import { AUTH_TRANSITION_FALLBACK_SVG_MARKUP } from './authTransitionLogoFallback';
import './auth-transition.css';

/** Stall antes de creep; tick un poco más ágil sin saltos grandes (tope IDLE_CAP intacto). */
const IDLE_STALL_MS = 900;
const IDLE_TICK_MS = 450;

/**
 * Splash unificado LoginPortal ↔ tenant WMS.
 * Visual fijo (dialecto boot): león solo + barra slide indeterminada.
 * Lifecycle (hitos/TTL/clear) sigue en sessionStorage; logoSrc se ignora a propósito
 * para no mezclar wordmark empresa / barra %.
 * @param {{
 *   logoSrc?: string,
 *   logoAlt?: string,
 *   ariaLabel?: string,
 *   fadeOut?: boolean,
 *   className?: string,
 * }} props
 */
export default function AuthTransitionSplash({
  logoSrc: _logoSrc,
  logoAlt = 'Logo',
  ariaLabel = 'Cargando',
  fadeOut = false,
  className = '',
}) {
  const [progress, setProgress] = useState(() => {
    const stored = readLoginTransitionProgress();
    return stored > 0 ? stored : AUTH_TRANSITION_MILESTONES.START;
  });
  const [entered, setEntered] = useState(false);
  const lastBumpRef = useRef(Date.now());
  const progressRef = useRef(progress);
  progressRef.current = progress;
  // `progress` = lifecycle (creep/hitos); la barra es CSS slide.

  useEffect(() => {
    const id = window.requestAnimationFrame(() => {
      setEntered(true);
      try {
        window.dispatchEvent(new CustomEvent(AUTH_TRANSITION_PAINTED_EVENT));
      } catch {
        /* ignore */
      }
    });
    return () => window.cancelAnimationFrame(id);
  }, []);

  useEffect(() => {
    if (fadeOut) return undefined;

    const sync = () => {
      const stored = readLoginTransitionProgress();
      if (stored > 0) {
        lastBumpRef.current = Date.now();
        setProgress((p) => Math.max(p, stored));
      }
    };
    sync();
    window.addEventListener(AUTH_TRANSITION_EVENT, sync);

    const intervalId = window.setInterval(() => {
      const stored = readLoginTransitionProgress();
      const stalled = Date.now() - lastBumpRef.current >= IDLE_STALL_MS;
      if (!stalled) {
        if (stored > 0) setProgress((p) => Math.max(p, stored));
        return;
      }
      // Solo persistir creep si hay transición de login activa.
      // Splash genérico (TigerHub / bootstrap) no debe re-armar el overlay raíz.
      if (stored <= 0) {
        const localNext = Math.min(
          AUTH_TRANSITION_MILESTONES.IDLE_CAP,
          Math.max(progressRef.current, AUTH_TRANSITION_MILESTONES.START) + 1,
        );
        lastBumpRef.current = Date.now();
        setProgress((p) => Math.max(p, localNext));
        return;
      }
      if (stored >= AUTH_TRANSITION_MILESTONES.IDLE_CAP) return;
      const next = advanceLoginTransitionProgress(
        Math.max(progressRef.current, stored),
      );
      lastBumpRef.current = Date.now();
      setProgress((p) => Math.max(p, next));
    }, IDLE_TICK_MS);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener(AUTH_TRANSITION_EVENT, sync);
    };
  }, [fadeOut]);

  useEffect(() => {
    if (fadeOut) setProgress(AUTH_TRANSITION_MILESTONES.DONE);
  }, [fadeOut]);

  const rootClass = [
    'auth-transition-overlay',
    entered ? 'is-entered' : '',
    fadeOut ? 'fade-out' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={rootClass} role="status" aria-live="polite" aria-label={ariaLabel}>
      <div
        className="auth-transition-logo"
        role="img"
        aria-label={logoAlt}
        // SVG inline: pinta con la barra (sin delay de decode de <img data-uri>).
        dangerouslySetInnerHTML={{ __html: AUTH_TRANSITION_FALLBACK_SVG_MARKUP }}
      />
      <div className="auth-transition-bar" aria-hidden="true">
        <div className="auth-transition-progress" />
      </div>
    </div>
  );
}
